import { StrictMode, useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import SiteHeader from "./site/SiteHeader.jsx";
import SiteFooter from "./site/SiteFooter.jsx";
import HomePage from "./pages/HomePage.jsx";
import PropertyPage from "./pages/PropertyPage.jsx";
import IslandPage from "./pages/IslandPage.jsx";
import ResultsPage from "./pages/ResultsPage.jsx";
import CheckoutPage from "./pages/CheckoutPage.jsx";
import BookingPage from "./pages/BookingPage.jsx";
import ContactPage from "./pages/ContactPage.jsx";
import PrivacyPage from "./pages/PrivacyPage.jsx";
import TermsPage from "./pages/TermsPage.jsx";
import EditIndexPage from "./pages/EditIndexPage.jsx";
import CategoryPage from "./pages/CategoryPage.jsx";
import ArticlePage from "./pages/ArticlePage.jsx";
import AboutPage from "./pages/AboutPage.jsx";
import ListPropertyPage from "./pages/ListPropertyPage.jsx";
import { CATEGORIES, ARTICLES, findArticle, mergeArticles, articleHref } from "./data/articles.js";
import { SUPPORT_EMAIL } from "./site/LegalShell.jsx";
import { SITE, CONTEXT, WEBSITE_ID, ORG_ID, absUrl, orgRef, hawaiiPlace, guideNode, breadcrumbNode, itemListNode, useJsonLd, useHead } from "./lib/seo.js";

function Coming({ title }) {
  return (
    <div className="container-page py-32 text-center">
      <span className="eyebrow">Coming soon</span>
      <h1 className="section-title mt-3">{title}</h1>
      <p className="prose-body mt-3 mx-auto max-w-xl">This page is next up in the rebuild. The homepage is live — click around the header to explore what's already done.</p>
      <a href="/" className="btn-primary mt-8">Back to home</a>
    </div>
  );
}

const ISLAND_SLUGS = new Set(["oahu","maui","kauai","big-island","molokai","lanai"]);
const CATEGORY_SLUGS = new Set(Object.keys(CATEGORIES));

function pickRoute(path) {
  if (path === "/") return { kind: "home" };
  const segs = path.replace(/\/+$/, "").split("/").filter(Boolean);
  if (segs.length === 1 && ISLAND_SLUGS.has(segs[0])) return { kind: "island", slug: segs[0] };
  if (segs.length === 1 && segs[0] === "edit") return { kind: "edit-index" };
  if (segs.length === 1 && CATEGORY_SLUGS.has(segs[0])) return { kind: "category", category: segs[0] };
  if (segs.length === 2 && CATEGORY_SLUGS.has(segs[0])) return { kind: "article", category: segs[0], slug: segs[1] };
  if (segs[0] === "properties" && segs[1]) return { kind: "property", raw: segs[1] };
  if (segs[0] === "results" || segs[0] === "search") return { kind: "results" };
  if (segs[0] === "checkout") return { kind: "checkout" };
  if (segs[0] === "booking") return { kind: "booking" };
  if (segs[0] === "about") return { kind: "about" };
  if (segs[0] === "list-your-property") return { kind: "list-property" };
  if (segs[0] === "contact") return { kind: "contact" };
  if (segs[0] === "privacy") return { kind: "privacy" };
  if (segs[0] === "terms") return { kind: "terms" };
  return { kind: "not-found" };
}

// Structured data + <head> for routes that don't own a full page component
// yet (guides, static pages). Home, island, results, property and checkout
// pages manage their own head + JSON-LD, so this component stays out of the
// way for those (useHead(null) is a no-op).
//
// Editorial pages are typed Article + Guide with the TravelAgency as
// publisher: to Google this is a purchase-assisting guide on an OTA, not a blog.
function RouteSchema({ route }) {
  const [remote, setRemote] = useState(null);
  const editorial = route.kind === "edit-index" || route.kind === "category" || route.kind === "article";
  useEffect(() => {
    if (!editorial) return;
    let live = true;
    fetch("/api/edit/posts").then(r => r.ok ? r.json() : []).then(list => { if (live) setRemote(list); }).catch(() => {});
    return () => { live = false; };
  }, [editorial]);
  const articles = useMemo(() => mergeArticles(remote || [], ARTICLES), [remote]);

  const guidesCollection = (path, name, description, list) => ({
    "@context": CONTEXT,
    "@type": "CollectionPage",
    "@id": absUrl(path),
    url: absUrl(path),
    name,
    description,
    isPartOf: { "@id": WEBSITE_ID },
    publisher: { "@id": ORG_ID },
    about: hawaiiPlace(),
    mainEntity: itemListNode(list.map(a => ({ ...guideNode(a), "@context": undefined })), { name })
  });

  let head = null, data = null;
  switch (route.kind) {
    case "edit-index": {
      const name = "The Hawaiʻi Edit — guides to where to stay in Hawaiʻi";
      const description = "Travel guides from Aloha Rates, the Hawaiʻi online travel agency: when to go, where to stay, and how to book hotels and vacation rentals at real local rates.";
      head = { title: "The Hawaiʻi Edit | Aloha Rates", description, canonical: "/edit/" };
      data = [breadcrumbNode([{ name: "Home", path: "/" }, { name: "The Edit", path: "/edit/" }]), guidesCollection("/edit/", name, description, articles)];
      break;
    }
    case "category": {
      const label = CATEGORIES[route.category].label;
      const path = `/${route.category}/`;
      const list = articles.filter(a => a.category === route.category);
      const name = `${label} guides for Hawaiʻi stays`;
      const description = `${label} guides from Aloha Rates to help you pick and book the right Hawaiʻi hotel or vacation rental.`;
      head = { title: `${label} guides | Aloha Rates`, description, canonical: path };
      data = [breadcrumbNode([{ name: "Home", path: "/" }, { name: "The Edit", path: "/edit/" }, { name: label, path }]), guidesCollection(path, name, description, list)];
      break;
    }
    case "article": {
      const a = articles.find(x => x.category === route.category && x.slug === route.slug) || findArticle(route.category, route.slug);
      const label = CATEGORIES[route.category].label;
      if (a) {
        const path = articleHref(a);
        head = { title: `${a.title} | Aloha Rates`, description: a.excerpt || SITE.description, canonical: path };
        data = [
          breadcrumbNode([{ name: "Home", path: "/" }, { name: "The Edit", path: "/edit/" }, { name: label, path: `/${route.category}/` }, { name: a.title, path }]),
          guideNode(a, { path })
        ];
      } else {
        head = { title: "Guide not found | Aloha Rates", robots: "noindex" };
      }
      break;
    }
    case "list-property":
      head = {
        title: "List your Hawaiʻi property | Aloha Rates",
        description: "Own or manage a hotel, condo or vacation rental in Hawaiʻi? Tell Aloha Rates about it and reach travellers who are only looking for Hawaiʻi.",
        canonical: "/list-your-property/"
      };
      data = [
        breadcrumbNode([{ name: "Home", path: "/" }, { name: "List your property", path: "/list-your-property/" }]),
        { "@context": CONTEXT, "@type": "WebPage", "@id": absUrl("/list-your-property/"), url: absUrl("/list-your-property/"),
          name: "List your Hawaiʻi property", isPartOf: { "@id": WEBSITE_ID }, about: hawaiiPlace(), publisher: { "@id": ORG_ID } }
      ];
      break;
    case "about":
      head = { title: "Our story | Aloha Rates", description: SITE.description, canonical: "/about/" };
      data = { "@context": CONTEXT, "@type": "AboutPage", "@id": absUrl("/about/"), url: absUrl("/about/"), name: "About Aloha Rates", isPartOf: { "@id": WEBSITE_ID }, mainEntity: orgRef() };
      break;
    case "contact":
      head = { title: "Contact | Aloha Rates", description: "Get in touch with Aloha Rates, the Hawaiʻi online travel agency.", canonical: "/contact/" };
      data = [
        breadcrumbNode([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact/" }]),
        { "@context": CONTEXT, "@type": "ContactPage", "@id": absUrl("/contact/"), url: absUrl("/contact/"), name: "Contact Aloha Rates", isPartOf: { "@id": WEBSITE_ID },
          mainEntity: { ...orgRef(), email: SUPPORT_EMAIL,
            contactPoint: { "@type": "ContactPoint", contactType: "customer support", email: SUPPORT_EMAIL, availableLanguage: "English", areaServed: "US" } } }
      ];
      break;
    case "privacy":
      head = { title: "Privacy policy | Aloha Rates", description: "What Aloha Rates collects when you search and book a Hawaiʻi stay, why, and who else sees it.", canonical: "/privacy/" };
      data = { "@context": CONTEXT, "@type": "WebPage", "@id": absUrl("/privacy/"), url: absUrl("/privacy/"), name: "Privacy policy", isPartOf: { "@id": WEBSITE_ID } };
      break;
    case "terms":
      head = { title: "Terms of service | Aloha Rates", description: "The agreement between you and Aloha Rates when you book Hawaiʻi accommodation through this site.", canonical: "/terms/" };
      data = { "@context": CONTEXT, "@type": "WebPage", "@id": absUrl("/terms/"), url: absUrl("/terms/"), name: "Terms of service", isPartOf: { "@id": WEBSITE_ID } };
      break;
    case "booking":
      head = { title: "My booking | Aloha Rates", robots: "noindex, nofollow" };
      break;
    case "not-found":
      head = { title: "Page not found | Aloha Rates", robots: "noindex" };
      break;
    default:
      break;
  }
  useJsonLd("route-ldjson", data);
  useHead(head);
  return null;
}

function App() {
  const route = pickRoute(location.pathname);

  let content;
  switch (route.kind) {
    case "home":         content = <HomePage/>; break;
    case "island":       content = <IslandPage slug={route.slug}/>; break;
    case "property": {
      const propertyId = route.raw.match(/^(lp[a-zA-Z0-9]+)/)?.[1];
      content = propertyId ? <PropertyPage propertyId={propertyId}/> : <Coming title="Property not found"/>;
      break;
    }
    case "results":      content = <ResultsPage/>; break;
    case "checkout":     content = <CheckoutPage/>; break;
    case "booking":      content = <BookingPage/>; break;
    case "edit-index":   content = <EditIndexPage/>; break;
    case "category":     content = <CategoryPage category={route.category}/>; break;
    case "article":      content = <ArticlePage category={route.category} slug={route.slug}/>; break;
    case "about":        content = <AboutPage/>; break;
    case "list-property": content = <ListPropertyPage/>; break;
    case "contact":      content = <ContactPage/>; break;
    case "privacy":      content = <PrivacyPage/>; break;
    case "terms":        content = <TermsPage/>; break;
    default:             content = <Coming title="Page not found"/>;
  }

  // Full-viewport pages (results, checkout, booking) hide the footer so the
  // whole shell fits exactly in 100dvh — no page scrollbar sneaking in.
  const fullViewport = ["results", "checkout", "booking"].includes(route.kind);

  return <>
    <RouteSchema route={route}/>
    <SiteHeader/>
    <main>{content}</main>
    {!fullViewport && <SiteFooter/>}
  </>;
}

createRoot(document.getElementById("root")).render(<StrictMode><App/></StrictMode>);
